import { Component, inject, OnInit } from "@angular/core";
import { AbstractControl, FormArray, FormBuilder, ReactiveFormsModule, ValidationErrors, Validators } from "@angular/forms";
import { ActivatedRoute, Router } from "@angular/router";
import { NgGlyph } from "@ng-icons/core";
import { AppButtonComponent } from "../../../../Common/Components/Button/ButtonComponent";
import { AppCardComponent } from "../../../../Common/Components/Card/CardComponent";
import { FileUploaderBaseComponent } from "../../../../Common/Components/FileUploader/FileUploaderComponent";
import { FileUploaderItem } from "../../../../Common/Components/FileUploader/Dto/FileUploaderItem";
import { QtyInputSelectZeroDirective } from "../../../../Common/Components/QtyInput/QtyInputSelectZeroDirective";
import { RichTextEditorComponent } from "../../../../Common/Components/RichTextEditor/RichTextEditorComponent";
import { ToastEffects } from "../../../../Common/Components/Toast/State/ToastEffects";
import { formatDateNumeric } from "../../../../Common/Utility/UtilityDate";
import { getBackendErrorMessage } from "../../../../Common/Utility/UtilityError";
import { FileApi } from "../../../File/Api/FileApi";
import { TestimonyReportEntity, TestimonyReportStatus } from "../../Dto/TestimonyModel";
import { TestimonyStatusUpdateRequest } from "../../Dto/TestimonyStatusUpdateRequest";
import { TestimonyUpdateRequest } from "../../Dto/TestimonyUpdateRequest";
import { TestimonyEffects } from "../../State/TestimonyEffects";

@Component({
  selector: "app-testimony-edit",
  standalone: true,
  imports: [
    ReactiveFormsModule,
    AppButtonComponent,
    AppCardComponent,
    FileUploaderBaseComponent,
    NgGlyph,
    QtyInputSelectZeroDirective,
    RichTextEditorComponent,
  ],
  templateUrl: "./TestimonyEditPage.html",
  styleUrl: "./TestimonyEditPage.scss",
})
export class TestimonyEditPage implements OnInit {
  private readonly fb = inject(FormBuilder);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly testimonyEffects = inject(TestimonyEffects);
  private readonly toastEffects = inject(ToastEffects);
  private readonly fileApi = inject(FileApi);

  report: TestimonyReportEntity | null = null;
  isSaving = false;
  isSubmitting = false;
  uploadErrors: Record<number, string | null> = {};
  readonly progressStatusOptions: { value: TestimonyStatusUpdateRequest["status"]; label: string }[] = [
    { value: "Draft", label: "Draft" },
    { value: "Contacted", label: "Contacted" },
    { value: "PartialReport", label: "Partial report" },
    { value: "Unresponsive", label: "Unresponsive" },
  ];
  private readonly materialUsageValidator = (control: AbstractControl): ValidationErrors | null => {
    const requestedQty = Number(control.get("requestedQty")?.value ?? 0);
    const usedQty = Number(control.get("usedQty")?.value ?? 0);
    const leftQty = Number(control.get("leftQty")?.value ?? 0);

    if (usedQty + leftQty > requestedQty) {
      return { materialUsageExceeded: true };
    }

    return null;
  };

  form = this.fb.group({
    status: ["Draft" as TestimonyReportStatus, [Validators.required]],
    reportDate: ["", [Validators.required]],
    studentsEnrolled: [0, [Validators.required, Validators.min(0)]],
    studentsEnrolledMultiple: [0, [Validators.required, Validators.min(0)]],
    studentsCompleted: [0, [Validators.required, Validators.min(0)]],
    studentsSaved: [0, [Validators.required, Validators.min(0)]],
    ministryParticipate: [0, [Validators.required, Validators.min(0)]],
    volunteerCount: [0, [Validators.required, Validators.min(0)]],
    volunteerChild: [0, [Validators.required, Validators.min(0)]],
    materials: this.fb.array([]),
    testimonies: this.fb.array([]),
  });

  get materials(): FormArray {
    return this.form.get("materials") as FormArray;
  }

  get testimonies(): FormArray {
    return this.form.get("testimonies") as FormArray;
  }

  get isSubmitted(): boolean {
    return this.report?.status === "Submitted";
  }

  get canUpdateProgressStatus(): boolean {
    const status = this.form.value.status;
    return status === "Draft" || status === "Contacted" || status === "PartialReport" || status === "Unresponsive";
  }

  ngOnInit(): void {
    const id = this.route.snapshot.paramMap.get("id");
    if (!id) {
      this.goBack();
      return;
    }
    void this.loadReport(id);
  }

  async loadReport(id: string): Promise<void> {
    try {
      const report = await this.testimonyEffects.getReport(id);
      this.report = report;
      this.patchForm(report);
    } catch (exception: unknown) {
      this.toastEffects.show(getBackendErrorMessage(exception, "Unable to load testimony report."), "error");
      this.goBack();
    }
  }

  patchForm(report: TestimonyReportEntity): void {
    this.form.patchValue({
      status: report.status,
      reportDate: this.toDateInput(report.reportDate),
      studentsEnrolled: report.studentsEnrolled,
      studentsEnrolledMultiple: report.studentsEnrolledMultiple,
      studentsCompleted: report.studentsCompleted,
      studentsSaved: report.studentsSaved,
      ministryParticipate: report.ministryParticipate,
      volunteerCount: report.volunteerCount,
      volunteerChild: report.volunteerChild,
    });

    this.materials.clear();
    report.materials.forEach((material) => {
      this.materials.push(this.createMaterialFormGroup(material));
    });

    this.testimonies.clear();
    let entries = report.testimonies;
    if (!entries.length) {
      entries = [{ text: "", testimonyDate: this.toDateInput(new Date()), files: [] }];
    }
    entries.forEach((entry) => {
      this.testimonies.push(
        this.fb.group({
          text: [entry.text ?? ""],
          testimonyDate: [this.toDateInput(entry.testimonyDate), [Validators.required]],
          files: [entry.files ?? []],
        }),
      );
    });
  }

  addTestimony(): void {
    this.testimonies.push(
      this.fb.group({
        text: [""],
        testimonyDate: [this.toDateInput(new Date()), [Validators.required]],
        files: [[]],
      }),
    );
  }

  removeTestimony(index: number): void {
    if (this.testimonies.length <= 1) return;
    this.testimonies.removeAt(index);
  }

  async uploadFiles(index: number, files: globalThis.File[]): Promise<void> {
    this.uploadErrors[index] = null;
    try {
      const uploaded = await this.fileApi.uploadFiles(files, { folder: "testimonies" });
      const control = this.testimonies.at(index);
      const current = (control.get("files")?.value ?? []) as TestimonyReportEntity["testimonies"][number]["files"];
      control.patchValue({ files: [...current, ...uploaded] });
    } catch (exception: unknown) {
      this.uploadErrors[index] = getBackendErrorMessage(exception, "Unable to upload files.");
    }
  }

  removeFile(testimonyIndex: number, fileId: string): void {
    const control = this.testimonies.at(testimonyIndex);
    const current = (control.get("files")?.value ?? []) as TestimonyReportEntity["testimonies"][number]["files"];
    control.patchValue({ files: current.filter((file) => file.id !== fileId) });
  }

  async save(): Promise<TestimonyReportEntity | null> {
    if (!this.report) return null;
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      this.toastEffects.show("Please fill in all required report fields.", "error");
      return null;
    }

    this.isSaving = true;
    try {
      const updated = await this.testimonyEffects.updateReport(this.report.id, this.buildPayload());
      this.report = updated;
      this.patchForm(updated);
      this.toastEffects.show("Testimony report saved.", "success");
      return updated;
    } catch (exception: unknown) {
      this.toastEffects.show(getBackendErrorMessage(exception, "Unable to save testimony report."), "error");
      return null;
    } finally {
      this.isSaving = false;
    }
  }

  async updateStatus(): Promise<void> {
    if (!this.report || this.isSubmitted) return;
    if (!this.canUpdateProgressStatus) return;
    const status = this.form.value.status as TestimonyStatusUpdateRequest["status"];
    this.isSaving = true;
    try {
      const updated = await this.testimonyEffects.updateStatus(this.report.id, { status });
      this.report = updated;
      this.patchForm(updated);
      this.toastEffects.show("Testimony status updated.", "success");
    } catch (exception: unknown) {
      this.toastEffects.show(getBackendErrorMessage(exception, "Unable to update testimony status."), "error");
    } finally {
      this.isSaving = false;
    }
  }

  async submit(): Promise<void> {
    if (!this.report) return;
    const saved = await this.save();
    if (!saved) return;

    this.isSubmitting = true;
    try {
      const submitted = await this.testimonyEffects.submitReport(saved.id);
      this.report = submitted;
      this.patchForm(submitted);
      this.toastEffects.show("Testimony report submitted.", "success");
    } catch (exception: unknown) {
      this.toastEffects.show(getBackendErrorMessage(exception, "Unable to submit testimony report."), "error");
      await this.loadReport(saved.id);
    } finally {
      this.isSubmitting = false;
    }
  }

  goBack(): void {
    this.router.navigate(["/testimonies"]).catch(() => undefined);
  }

  getFileItems(index: number): FileUploaderItem[] {
    const files = (this.testimonies.at(index).get("files")?.value ?? []) as TestimonyReportEntity["testimonies"][number]["files"];
    return files.map((file) => ({
      id: file.id,
      name: file.originalName || file.name,
      size: file.size,
      status: "uploaded",
      publicUrl: file.publicUrl,
      mimeType: file.mimeType,
    }));
  }

  getMaterialTotal(field: "requestedQty" | "usedQty" | "leftQty"): number {
    return this.materials.controls.reduce((total, control) => total + Number(control.get(field)?.value ?? 0), 0);
  }

  formatDate(value?: string): string {
    if (value) return formatDateNumeric(value);
    return "-";
  }

  private buildPayload(): TestimonyUpdateRequest {
    return {
      reportDate: this.form.value.reportDate ?? "",
      studentsEnrolled: Number(this.form.value.studentsEnrolled ?? 0),
      studentsEnrolledMultiple: Number(this.form.value.studentsEnrolledMultiple ?? 0),
      studentsCompleted: Number(this.form.value.studentsCompleted ?? 0),
      studentsSaved: Number(this.form.value.studentsSaved ?? 0),
      ministryParticipate: Number(this.form.value.ministryParticipate ?? 0),
      volunteerCount: Number(this.form.value.volunteerCount ?? 0),
      volunteerChild: Number(this.form.value.volunteerChild ?? 0),
      materials: this.materials.controls.map((control) => ({
        materialId: control.get("materialId")?.value ?? "",
        requestedQty: Number(control.get("requestedQty")?.value ?? 0),
        usedQty: Number(control.get("usedQty")?.value ?? 0),
        leftQty: Number(control.get("leftQty")?.value ?? 0),
      })),
      testimonies: this.testimonies.controls.map((control) => {
        const files = (control.get("files")?.value ?? []) as TestimonyReportEntity["testimonies"][number]["files"];
        return {
          text: control.get("text")?.value ?? "",
          testimonyDate: control.get("testimonyDate")?.value ?? "",
          fileIds: files.map((file) => file.id),
        };
      }),
    };
  }

  private toDateInput(value: string | Date): string {
    return new Date(value).toISOString().slice(0, 10);
  }

  private createMaterialFormGroup(material: TestimonyReportEntity["materials"][number]) {
    const group = this.fb.group(
      {
        materialId: [material.materialId, [Validators.required]],
        materialLabel: [`${material.material.sequence}. ${material.material.name}`],
        sku: [material.material.sku],
        requestedQty: [material.requestedQty, [Validators.required, Validators.min(0)]],
        usedQty: [material.usedQty, [Validators.required, Validators.min(0)]],
        leftQty: [material.leftQty, [Validators.required, Validators.min(0)]],
        dcmSeriesId: [material.dcmSeriesId],
      },
      { validators: this.materialUsageValidator },
    );

    group.get("usedQty")?.valueChanges.subscribe(() => {
      this.syncLeftQty(group);
    });

    this.syncLeftQty(group);
    return group;
  }

  private syncLeftQty(control: AbstractControl): void {
    const requestedQty = Number(control.get("requestedQty")?.value ?? 0);
    const usedQty = Number(control.get("usedQty")?.value ?? 0);
    const nextLeftQty = Math.max(requestedQty - usedQty, 0);
    control.get("leftQty")?.setValue(nextLeftQty, { emitEvent: false });
    control.updateValueAndValidity({ emitEvent: false });
  }
}
